export type SortSelectOption = {
  value: string;
  label: string;
};

type Props = {
  id: string;
  label: string;
  options: SortSelectOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

/**
 * The site's one "Sort by" control.
 *
 * Deliberately agnostic about what a value *is*, because the two listings that have one
 * disagree and both are right: on `/reviews` ordering is an API parameter and the value
 * names a field, while on a category page it is a url and choosing one navigates. Either
 * way this renders a labelled select and hands the chosen value back, and the page
 * decides what that means.
 *
 * A native `<select>` rather than the shared `Select` listbox: it is one control in a
 * toolbar, the phone's own picker is better than anything we would draw for it, and the
 * legacy category page this replaced was a native select too — so the keyboard and
 * screen-reader behaviour a visitor already knows is kept.
 *
 * Nothing to choose between is not a control, so one option or none renders nothing.
 */
export default function SortSelect({ id, label, options, value, onChange, className }: Props) {
  if (options.length < 2) {
    return null;
  }

  return (
    <div className={className || 'flex items-center gap-2 text-sm text-slate-600'}>
      <label htmlFor={id}>{label}</label>

      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-solid border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-900"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
